<template>
  <v-app>
    <v-main class="main">

      <!-- Sidebar -->
      <AdminSidebar :drawerOpen="drawerOpen" :collapsed="isSidebarCollapsed" :isMobile="isMobile"
        @update:drawerOpen="drawerOpen = $event" />

      <v-container fluid class="main-content pa-4">

        <!-- Header -->
        <Header :isDarkMode="isDarkMode" :isMobile="isMobile" @toggle-sidebar="toggleSidebar" />

        <!-- Stats -->
        <v-row class="stats mt-4" dense>
          <v-col cols="12" sm="6">
            <StatCard title="Active Products" :value="activeProducts" icon="mdi-check-circle-outline"
              iconColor="#22c55e" />
          </v-col>
          <v-col cols="12" sm="6">
            <StatCard title="Inactive Products" :value="inactiveProducts" icon="mdi-close-circle-outline"
              iconColor="#ef4444" />
          </v-col>
        </v-row>

        <!-- Table Card -->
        <v-card class="table-card mt-6">

          <div class="table-header d-flex justify-space-between align-center flex-wrap ga-3">
            <!-- Left side: search + clear -->
            <div style="display: flex; align-items: center; gap: 16px; margin-bottom: 8px;">
              <InputText v-model="filters['global'].value" placeholder="Search products..." />
              <Button type="button" icon="pi pi-filter-slash" label="Clear" variant="outlined" @click="clearFilters" />
            </div>
            <button type="button" :disabled="loading" class="theme-button px-5" @click="openCreateProduct">
              <span v-if="!loading"><v-icon start>mdi-plus</v-icon> Add New Product </span>
            </button>
          </div>

          <!-- PrimeVue DataTable with search ---NB: add showGridlines to show column lines-->
          <DataTable v-model:filters="filters" :value="products" paginator :rows="10" dataKey="id" stripedRows
            class="modern-table" :rowsPerPageOptions="[5, 10, 20, 50]" tableStyle="min-width: 50rem"
            filterDisplay="menu" @row-click="goToDetails" selectionMode="single"
            :globalFilterFields="['product_name', 'description']">

            <!-- Empty table -->
            <template #empty>
              No products found.
            </template>

            <!-- Loading state -->
            <template #loading>
              Loading products...
            </template>

            <!-- Product Column -->
            <Column header="Product">
              <template #body="slotProps">
                <div class="product-cell">
                  <img
                    :src="slotProps.data.product_image ? `${STORAGE_URL}/${slotProps.data.product_image}` : defaultImage"
                    class="product-image" />
                  <div>
                    <div class="product-name">{{ slotProps.data.product_name }}</div>
                    <!-- <div class="product-desc">{{ truncateHtml(slotProps.data.description) }}</div> -->
                     <div class="product-desc" v-html="slotProps.data.description"></div>
                  </div>
                </div>
              </template>
            </Column>

            <!-- Status Column -->
            <Column header="Status">
              <template #body="slotProps">
                <span :class="['status-pill', slotProps.data.is_active ? 'status-active' : 'status-inactive']">
                  {{ slotProps.data.is_active ? 'Active' : 'Inactive' }}
                </span>
              </template>
            </Column>

            <!-- Start Column -->
            <Column header="Start Date">
              <template #body="slotProps">
                {{ formatDate(slotProps.data.start_date) }}
              </template>
            </Column>

            <!-- Maturity Column -->
            <Column header="Maturity Date">
              <template #body="slotProps">
                {{ formatDate(slotProps.data.maturity_date) }}
              </template>
            </Column>

            <!-- Expiry Column -->
            <Column header="Expiry Date">
              <template #body="slotProps">
                {{ formatDate(slotProps.data.expiry_date) }}
              </template>
            </Column>

            <!-- Available Volume Column-->
            <Column header="Available Volume">
              <template #body="slotProps">
                {{ formatNumber(slotProps.data.available_volume) }}
              </template>
            </Column>

            <!-- Purchased Volume Column-->
            <!-- <Column header="Purchased Volume">
              <template #body="slotProps">
                {{ formatNumber(slotProps.data.purchased_volume) }}
              </template>
            </Column> -->

            <!-- Balance withdrawn Column-->
            <Column header="Balance WIthdrawn">
              <template #body="slotProps">
                {{ formatCurrency(slotProps.data.balance_withdrawn) }}
              </template>
            </Column>

            <!-- Interest Column -->
            <Column header="Interest(%)">
              <template #body="slotProps">
                {{ parseFloat(slotProps.data.interest_rate) }}%
              </template>
            </Column>

            <!-- Created Column -->
            <Column header="Created">
              <template #body="slotProps">
                {{ formatDate(slotProps.data.created_at) }}
              </template>
            </Column>

            <!-- Actions Column -->
            <Column header="" style="width:120px; border-radius: 10px;">
              <template #body="slotProps">
                <v-menu>
                  <template #activator="{ props }">
                    <v-btn icon v-bind="props" variant="text">
                      <v-icon>mdi-dots-horizontal</v-icon>
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item @click="openEditModal(slotProps.data)">
                      Edit {{ slotProps.data.product_name }} Details
                    </v-list-item>
                  </v-list>

                </v-menu>
              </template>
            </Column>

          </DataTable>
        </v-card>
        <v-dialog v-model="productDialog" max-width="750">
          <v-card class="pa-6">

            <v-card-title class="text-h6 mb-4 text-center">
              {{ isEditing ? 'Edit Product' : 'Create Product' }}
            </v-card-title>

            <form @submit.prevent="handleCreateProduct" class="space-y-5">

              <!-- Product Name -->
              <div class="flex flex-col gap-2">
                <label class="text-sm font-medium">Product Name</label>
                <v-text-field v-model="form.product_name" variant="outlined" placeholder="Enter product name" />
              </div>

              <!-- Description -->
              <div class="flex flex-col gap-2">
                <label class="text-sm font-medium">Description</label>
                <!-- <v-textarea v-model="form.description" variant="outlined" placeholder="Enter product description"
                  rows="3" /> -->
                <Editor v-model="form.description" editorStyle="height: 220px" placeholder="Enter product description" />
              </div>

              <!-- Ivestment Note -->
              <div class="flex flex-col gap-2">
                <label class="text-sm font-medium">Investment Note</label>
                <!-- <v-textarea v-model="form.investment_note" variant="outlined" placeholder="Enter Investment Note"
                  rows="3" /> -->
                <Editor v-model="form.investment_note" editorStyle="height: 220px" placeholder="Enter Investment Note" />
              </div>

              <!-- Product Owner -->
              <div class="flex flex-col gap-2 mt-2">
                <!-- <label class="text-sm font-medium">Product Owner</label> -->

                <v-select v-model="form.partner_type_id" :items="partnerOptions" item-title="title" item-value="value"
                  variant="outlined" label="Select Product Owner/Partner" placeholder="Select Product Owner/Partner" />
              </div>

              <!-- Product Owner -->
              <div class="flex flex-col gap-2 mt-2">
                <v-select v-model="form.product_type_id" :items="productOptions" item-title="title" item-value="value"
                  variant="outlined" label="Select Product Type" placeholder="Product Type" />
              </div>

              <!-- Start Date -->
              <div class="flex flex-col gap-2">
                <label class="text-sm font-medium">Start Date</label>
                <v-text-field v-model="form.start_date" type="date" variant="outlined" :min="today" />
              </div>

              <!-- Maturity Date -->
              <div class="flex flex-col gap-2">
                <label class="text-sm font-medium">Maturity Date</label>
                <v-text-field v-model="form.maturity_date" type="date" variant="outlined" :min="today" />
              </div>

              <!-- Expiry Date -->
              <div class="flex flex-col gap-2">
                <label class="text-sm font-medium">Expiry Date</label>
                <v-text-field v-model="form.expiry_date" type="date" variant="outlined" />
              </div>

              <!-- Volumes -->
              <div class="grid grid-cols-2 gap-3">

                <v-text-field v-model="form.available_volume" type="number" variant="outlined"
                  label="Available Volume" />

                <!-- <v-text-field v-model="form.purchased_volume" type="number" variant="outlined"
                  label="Purchased Volume" /> -->
              </div>

              <!-- Amount -->
              <div class="flex flex-col gap-2">
                <label class="text-sm font-medium">Minumum Amount</label>
                <v-text-field v-model="form.minimum_amount" type="number" variant="outlined" placeholder="e.g 1005" />
              </div>

              <!--tenure-->
              <div class="flex flex-col gap-2">
                <label class="text-sm font-medium">Minumum Tenure</label>
                <v-text-field v-model="form.minimum_tenure" type="number" variant="outlined" placeholder="e.g 5" />
              </div>

              <!-- Interest -->
              <div class="flex flex-col gap-2">
                <label class="text-sm font-medium">Interest Rate (%)</label>
                <v-text-field v-model="form.interest_rate" type="number" variant="outlined" placeholder="e.g 12.5" />
              </div>

              <!-- Image -->
              <div class="flex flex-col gap-2">
                <label class="text-sm font-medium">Product Image</label>
                <v-file-input v-model="form.product_image" variant="outlined" accept="image/*"
                  label="Upload product image" prepend-icon="mdi-camera" />
                <img v-if="existingImage" :src="`${STORAGE_URL}/${existingImage}`" class="mt-3 rounded" width="120" />
              </div>

              <!-- Status -->
              <!-- <div class="flex flex-col gap-2">
                <label class="text-sm font-medium">Status</label>
                <v-switch v-model="form.is_active" color="green" :label="form.is_active ? 'Active' : 'Inactive'" />
              </div> -->

              <v-row>

                <!-- Status -->
                <v-col cols="6">
                  <div class="flex flex-col gap-2">
                    <label class="text-sm font-medium">Status</label>

                    <v-switch v-model="form.is_active" color="green" :label="form.is_active ? 'Active' : 'Inactive'" />
                  </div>
                </v-col>

                <!-- Featured -->
                <v-col cols="6">
                  <div class="flex flex-col gap-2">
                    <label class="text-sm font-medium">Featured</label>

                    <v-switch v-model="form.featured" color="green"
                      :label="form.featured ? 'Featured' : 'Not Featured'" />
                  </div>
                </v-col>

              </v-row>

              <!-- Actions -->
              <div class="flex justify-center gap-3 pt-4"
                style="display:flex; justify-content:center; align-items:center; gap:12px; padding-top:16px;">
                <v-btn variant="outlined" @click="productDialog = false">
                  Cancel
                </v-btn>

                <v-btn color="black" type="submit" :loading="loading">
                  {{ isEditing ? 'Update Product' : 'Save Product' }}
                </v-btn>
              </div>

            </form>
          </v-card>
        </v-dialog>
        <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000" location="top right"> {{
          snackbar.message }}
          <template #actions>
            <v-btn variant="text" @click="snackbar.show = false">
              Close
            </v-btn>
          </template>
        </v-snackbar>
      </v-container>
    </v-main>
  </v-app>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from "vue"
import { useRouter } from "vue-router"
import AdminSidebar from "@/components/AdminSidebar.vue"
import Header from "@/components/Header.vue"
import StatCard from "@/components/StatCard.vue"
import { useProductStore } from "@/store/product"
import { STORAGE_URL } from "@/api/env"
import avatarImage from "@/assets/icon.png"
import { formatNumber, formatDate, formatCurrency } from "@/util"
import { updateProduct, createProduct } from "@/api/product"
import { partnerStore } from "@/store/partner"

//const auth = useAuthStore()
const prd = useProductStore()
const pStore = partnerStore()
const router = useRouter()

const isSidebarCollapsed = ref(false)
const drawerOpen = ref(false)
const isMobile = ref(false)
const filters = ref({ global: { value: null, matchMode: "contains" } })
const defaultImage = avatarImage
const productDialog = ref(false)
const loading = ref(false)
const isEditing = ref(false)
const editingId = ref(null)
const existingImage = ref("")

/* Mobile */
const toggleSidebar = () => {
  if (isMobile.value) drawerOpen.value = !drawerOpen.value
  else isSidebarCollapsed.value = !isSidebarCollapsed.value
}

const checkMobile = () => {
  isMobile.value = window.innerWidth < 768
  drawerOpen.value = !isMobile.value
}

/* Products */
const products = computed(() => prd.products?.products || [])

/* Stats */
const activeProducts = computed(() => products.value.filter(p => p.is_active).length)
const inactiveProducts = computed(() => products.value.filter(p => !p.is_active).length)

//snackbar
const snackbar = ref({
  show: false,
  message: "",
  color: "success" // success | error | warning | info
})

const showSnackbar = (message, color = "success") => {
  snackbar.value.message = message
  snackbar.value.color = color
  snackbar.value.show = true
}

const form = ref({
  product_name: "",
  description: "",
  product_image: null,
  is_active: true,
  investment_note: "",
  start_date: "",
  maturity_date: "",
  expiry_date: "",
  available_volume: null,
  minimum_tenure: null,
  minimum_amount: null,
  interest_rate: null,
  partner_type_id: "",
  product_type_id: "",
  featured: false,
})

const resetForm = () => {
  form.value = {
    product_name: "",
    description: "",
    product_image: null,
    is_active: true,
    investment_note: "",
    start_date: "",
    maturity_date: "",
    expiry_date: "",
    available_volume: "",
    minimum_tenure: "",
    minimum_amount: "",
    interest_rate: "",
    partner_uuid: "",
    product_type_uuid: "",
    featured: false,
  }
}

watch(
  [() => form.value.start_date, () => form.value.maturity_date],
  ([startDate, maturityDate]) => {
    // If either date is missing
    if (!startDate || !maturityDate) {
      form.value.minimum_tenure = ''
      return
    }

    const start = new Date(`${startDate}T00:00:00`)
    const end = new Date(`${maturityDate}T00:00:00`)

    const difference = end.getTime() - start.getTime()

    const days = Math.ceil(
      difference / (1000 * 60 * 60 * 24)
    )

    form.value.minimum_tenure = days > 0 ? days : 0
  }
)

const openCreateProduct = () => {
  isEditing.value = false
  editingId.value = null
  resetForm()
  existingImage.value = ""
  productDialog.value = true
}

const partnerOptions = computed(() => {
  return (pStore.partners || []).map(partner => ({
    title: partner.name,
    value: partner.uuid
  }))
})

const productOptions = computed(() => {
  return (prd.product_type || []).map(partner => ({
    title: partner.name,
    value: partner.uuid
  }))
})

const handleCreateProduct = async () => {
  loading.value = true

  try {
    const payload = new FormData()

    payload.append("product_name", form.value.product_name)
    payload.append("description", form.value.description)
    payload.append("partner_type_id", form.value.partner_type_id)
    payload.append("product_type_id", form.value.product_type_id)
    payload.append("investment_note", form.value.investment_note)
    payload.append("minimum_tenure", form.value.minimum_tenure)
    payload.append("minimum_amount", form.value.minimum_amount)
    //payload.append("is_active", form.value.is_active ? 1 : 0)
    payload.append("featured", form.value.is_active ? 1 : 0)
    payload.append("start_date", form.value.start_date)
    payload.append("maturity_date", form.value.maturity_date)
    payload.append("expiry_date", form.value.expiry_date)
    payload.append("available_volume", form.value.available_volume)
    payload.append("interest_rate", form.value.interest_rate)

    if (form.value.product_image) {
      payload.append("product_image", form.value.product_image)
    }

    if (isEditing.value) {
      await updateProduct(editingId.value, payload)
    } else {
      await createProduct(payload)
    }

    await prd.loadProducts()

    showSnackbar(
      isEditing.value
        ? "Product updated successfully"
        : "Product created successfully"
    )

    setTimeout(() => { productDialog.value = false }, 2000)
    resetForm()

  } finally {
    loading.value = false
  }
}

//edit modal
const openEditModal = (item) => {
  isEditing.value = true
  editingId.value = item.uuid

  form.value.product_name = item.product_name || ""
  form.value.description = item.description || ""

  form.value.partner_type_id = item.partner_type_id || ""
  form.value.product_type_id = item.product_type_id || ""
  form.value.is_active = item.is_active ?? true
  form.value.start_date = item.start_date || ""
  form.value.maturity_date = item.maturity_date || ""
  form.value.expiry_date = item.expiry_date || ""
  form.value.available_volume = item.available_volume || ""
  form.value.minimum_tenure = item.minimum_tenure || ""
  form.value.minimum_amount = item.minimum_amount || ""
  form.value.investment_note = item.investment_note || ""
  form.value.balance_withdrawn = item.balance_withdrawn || ""
  form.value.interest_rate = item.interest_rate || ""
  form.value.featured = item.featured ?? false

  // reset file input
  form.value.product_image = null

  // existing image preview
  existingImage.value = item.product_image || ""

  productDialog.value = true
}

/* Lifecycle */
onMounted(async () => {
  checkMobile()
  window.addEventListener("resize", checkMobile)

  try {
    await prd.loadProducts()
    await prd.loadProductTypes()
    await pStore.loadPartners()
  } catch (error) {
    console.error(error)
  }
})

onUnmounted(() => {
  window.removeEventListener("resize", checkMobile)
})

/* Clear filters */
const clearFilters = () => {
  filters.value = { global: { value: null, matchMode: "contains" } }
}

//clicking on the row
const goToDetails = (event) => {
  const row = event.data

  router.push({
    name: 'ProductDetails',
    params: { id: row.uuid }
  })
}
</script>

<style scoped>
.main {
  display: flex;
  min-height: 100vh;
}

.main-content {
  flex: 1;
  background: #f8fafc;
}

/* Card */
.table-card {
  padding: 20px;
  border-radius: 14px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
}

/* Header */
.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

/* Table */
.modern-table {
  border: none;
}

/* Product */
.product-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.product-image {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  object-fit: cover;
}

.product-name {
  font-weight: 700;
}

.product-desc {
  font-size: 16px;
  color: #64748b;
  max-height: 100px;
  max-width: 400px;
  overflow: hidden;
}

/* Status */
.status-pill {
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}

.status-active {
  background: #ecfdf5;
  color: #16a34a;
}

.status-inactive {
  background: #fef2f2;
  color: #dc2626;
}

/* Search input */
.search-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 8px;
  color: #64748b;
}

.search-wrapper input {
  padding-left: 28px;
}

/* Button */
.theme-button {
  height: 46px;
  background: #000;
  color: #fff;
  border-radius: 10px;
  font-weight: 600;
  transition: all 0.25s ease;
}

:deep(.modern-table .p-datatable-thead > tr > th) {
  background: #eef7f0;
  color: #065f46;
  font-weight: 600;
  border-bottom: 2px solid #c1edc3;
}

.p-datatable-tbody>tr {
  cursor: pointer;
}

.p-datatable-tbody>tr:hover {
  background-color: #f5f5f5;
}
</style>
